import { json, LoaderFunction, redirect } from '@remix-run/node';
import { useLoaderData, useNavigate, Link } from '@remix-run/react';
import type { MetaFunction } from '@remix-run/node';
import type { Product } from '~/api/typesense.server';
import { getProductBySlug } from '~/api/typesense.server';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useCart } from '~/context/cart-context';
import { toast } from 'react-hot-toast';
import { getFillerImage } from '~/utils/get-filler-image';
import { useBrandLogoCache } from '~/context/brand-logo-context';
import { analytics } from '~/utils/analytics';
import { parse } from 'cookie';
import SuggestedProductsSlider from '~/components/product-modal/suggested-product-slider';
import ShopNearbyDispensaries from '~/components/shop-nearby-dispensaries';
import FaqsSection from '~/routes/_layout.dispensary-locations.$location/components/faqs-section';
import LowStockAlert from '~/components/low-stock-alert';
import { useStickyOptimized } from '~/hooks/use-sticky-optimized';
import ProductReviewModal from '~/components/product-review-modal';
import ProductReviews from '~/components/product-reviews';
import ProductVariants from '~/components/product-modal/product-variants';
import { useReviewCache } from '~/context/review-context';
import { parseCartError, isErrorAlreadyHandled } from '~/utils/cart-error-handler';

interface Store {
  name: string;
  value: string;
  address: string;
  hours: string;
  url: string;
}

const stores: Store[] = [
  {
    name: 'ANTIOCH',
    value: 'catalystantioch',
    address: '2625 Crow Ct, Antioch, CA 94509',
    url: '/dispensary-locations/antioch-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'BELLFLOWER',
    value: 'catalystbellflower',
    address: '9032 Artesia Blvd, Bellflower, CA 90706',
    url: '/dispensary-locations/bellflower-dispensary/',
    hours: 'Open 9am - 9pm',
  },
  {
    name: 'BELMONT SHORE',
    value: 'catalystbelmont',
    address: '5227 E 2nd St, Long Beach, CA 90803',
    url: '/dispensary-locations/belmont-shore-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'CHERRY',
    value: 'catalyst405',
    address: '3170 Cherry Ave, Long Beach, CA 90807',
    url: '/dispensary-locations/cherry-ave-long-beach-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'COSTA MESA',
    value: 'catalystcostamesa',
    address: '170 E 17th St Ste 115, Costa Mesa, CA 92627',
    url: '/dispensary-locations/costa-mesa-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'DALY CITY',
    value: 'catalystdalycity',
    address: '1000 King Dr Ste 200, Daly City, CA 94015',
    url: '/dispensary-locations/daly-city-dispensary/',
    hours: 'Open 10am - 9.30pm',
  },
  {
    name: 'DOWNTOWN LONG BEACH',
    value: 'catalystpine',
    address: '433 Pine Street, Ste 500, Long Beach, CA 90802',
    url: '/dispensary-locations/downtown-long-beach-dispensary/',
    hours: 'Open 10.30am - 9.30pm',
  },
  {
    name: 'EL MONTE',
    value: 'catalystelmonte',
    address: '12154 Valley Blvd, El Monte, CA 91732',
    url: '/dispensary-locations/el-monte-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'FIGUEROA',
    value: 'catalystfigueroa',
    address: '10020 S Figueroa St, Los Angeles, CA 90003',
    url: '/dispensary-locations/figueroa-los-angeles-dispensary/',
    hours: 'Open 10am - 9.30pm',
  },
  {
    name: 'FLORENCE',
    value: 'catalystflorence',
    address: '316 W Florence Avenue, Los Angeles, CA 90003',
    url: '/dispensary-locations/florence-los-angeles-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'GROVER BEACH',
    value: 'catalystgroverbeach',
    address: '1053 Highland Way, Grover Beach, CA 93433',
    url: '/dispensary-locations/grover-beach-dispensary/',
    hours: 'Open 9am - 9pm',
  },
  {
    name: 'HAWTHORNE',
    value: 'catalysthawthorne',
    address: '14115 Crenshaw BLVD #9, Hawthorne, CA 90250',
    url: '/dispensary-locations/hawthorne-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'HEMET',
    value: 'catalysthemet',
    address: '41007 E Florida Ave, Hemet, CA 92544',
    url: '/dispensary-locations/hemet-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'LANCASTER',
    value: 'catalystlancaster',
    address: '42020 4th St E, Lancaster, CA 93535',
    url: '/dispensary-locations/lancaster-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'LYNWOOD',
    value: 'catalystlynwood',
    address: '5110 E Imperial Hwy, Lynwood, CA 90262',
    url: '/dispensary-locations/lynwood-dispensary/',
    hours: 'Open 9am-9:30pm',
  },
  {
    name: 'MARINA',
    value: 'catalystmarina',
    address: '3100 Del Monte Blvd, Marina, CA 93933',
    url: '/dispensary-locations/marina-dispensary/',
    hours: 'Open 10am - 9.30pm',
  },
  {
    name: 'MID CITY',
    value: 'catalystmidcity',
    address: '3318 Venice Blvd, Los Angeles, CA 90019',
    url: '/dispensary-locations/mid-city-los-angeles-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'MORENO VALLEY',
    value: 'catalystmorenovalley',
    address: '24703 Alessandro Blvd, Moreno Valley, CA 92553',
    url: '/dispensary-locations/moreno-valley-dispensary/',
    hours: 'Open 10am - 9.30pm',
  },
  {
    name: 'NORMANDIE',
    value: 'catalystnormandie',
    address: '8300 S Normandie Ave, Los Angeles, CA 90044',
    url: '/dispensary-locations/normandie-los-angeles-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'OC3',
    value: 'oc3',
    address: '3122 S Halladay St Unit A, Santa Ana, CA 92705',
    url: '/dispensary-locations/oc3-santa-ana-dispensary/',
    hours: 'Open 9am-9:30pm',
  },
  {
    name: 'OXNARD',
    value: 'catalystoxnard',
    address: '4749 S Rose Ave, Oxnard, CA 93033',
    url: '/dispensary-locations/oxnard-dispensary/',
    hours: 'Open 9am - 9pm',
  },
  {
    name: 'PALM DESERT',
    value: 'catalystpalmdesert',
    address: '39420 Berkey Dr, Palm Desert, CA 92211',
    url: '/dispensary-locations/palm-desert-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'PATTERSON',
    value: 'catalystpatterson',
    address: '100 W Las Palmas Ave, Patterson, CA 95363',
    url: '/dispensary-locations/patterson-dispensary/',
    hours: 'Open 10am - 9pm',
  },
  {
    name: 'POMONA',
    value: 'catalystpomona',
    address: '456 E Holt Ave, Pomona, CA 91767',
    url: '/dispensary-locations/pomona-dispensary/',
    hours: 'Open 9am - 9.50pm',
  },
  {
    name: 'SANTA ANA',
    value: 'catalystsantaana',
    address: '2400 Pullman St, Santa Ana, CA 92705',
    url: '/dispensary-locations/santa-ana-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'SAN BERNARDINO',
    value: 'catalystsbinland',
    address: '506 Inland Center Dr, San Bernardino, CA 92408',
    url: '/dispensary-locations/san-bernadino-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'SAN DIEGO',
    value: 'catalystsandiego',
    address: '5125 Convoy St, STE 211, San Diego, CA 92111',
    url: '/dispensary-locations/san-diego-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'SILVER LAKE',
    value: 'catalystsilverlake',
    address: '2334 Fletcher Dr, Los Angeles, CA 90039',
    url: '/dispensary-locations/silver-lake-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'STANTON',
    value: 'catalyststanton',
    address: '8130 Monroe Ave, Stanton, CA 90680',
    url: '/dispensary-locations/stanton-dispensary/',
    hours: 'Open 9am - 9.30pm',
  },
  {
    name: 'VAN NUYS',
    value: 'catalystvannuys',
    address: '8162 Orion Ave, Van Nuys, CA 91406',
    url: '/dispensary-locations/van-nuys-dispensary/',
    hours: 'Open 10am - 9.30pm',
  },
  {
    name: 'WATSONVILLE',
    value: 'catalystwatsonville',
    address: '1324 W Beach St, Watsonville, CA 95076',
    url: '/dispensary-locations/watsonville-dispensary/',
    hours: 'Open 10am - 9pm',
  },
];

// WordPress location interface - matching the category page
interface WordPressLocation {
  title: string;
  slug: string;
  locationDetails: {
    storeImage?: {
      node: {
        mediaItemUrl: string;
        altText: string;
      };
    };
    address: string;
    addressmap?: {
      latitude: number;
      longitude: number;
    };
    openingHours: string;
    phone: string;
    email: string;
    comingSoon?: string[];
    faqs?: Array<{
      question: string;
      answer: string;
    }>;
    reviewsWidget?: string;
  };
}

interface Review {
  id: string;
  user_id: string;
  product_id: string;
  rating: number;
  comment: string;
  created: string;
  status: string;
  user: {
    created_at: string;
    email: string;
    raw_app_meta_data: string;
  };
}

interface LoaderData {
  product: Product;
  availableStores: Store[];
  defaultAvailable: boolean;
  store: string;
  allLocations: WordPressLocation[];
  currentStoreLocation: WordPressLocation | null;
  productUrl: string;
}

export const meta: MetaFunction = ({ data, params, location }) => {
  const { product } = data as LoaderData;
  const productName = product?.name || params.slug;
  const brand = product?.brand || 'Unknown Brand';
  const rawTitle = `${productName} - ${brand}`;
  const siteName = 'Catalyst Cannabis';
  const finalTitle = `${rawTitle} - ${siteName}`;

  const subtype = product?.subtype || 'Unknown Subtype';
  const category = product?.category || 'Unknown Category';
  const description = `Explore our ${subtype} ${category} product: ${productName} by ${brand}. Discover detailed information, specifications, and benefits at Catalyst Cannabis.`;
  const canonicalUrl = `https://catalyst-cannabis.com/products/${params.slug}`;

  return [
    { title: finalTitle },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    {
      name: 'robots',
      content: 'follow, index, max-snippet:50, max-video-preview:-1, max-image-preview:large',
    },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:type', content: 'product' },
    { property: 'og:title', content: finalTitle },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: siteName },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: finalTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: '@Catalyst_WFTP' },
    { name: 'twitter:creator', content: '@Catalyst_WFTP' },
    { name: 'twitter:label2', content: 'Time to read' },
    { name: 'twitter:data2', content: 'Less than a minute' },
  ];
};

// Function to fetch all WordPress locations for nearby dispensaries
async function fetchAllLocations(): Promise<WordPressLocation[]> {
  const graphqlQuery = `
    query GetAllLocations {
      allLocation(first: 100) {
        nodes {
          title
          slug
          locationDetails {
            storeImage {
              node {
                mediaItemUrl
                altText
              }
            }
            address
            addressmap {
              latitude
              longitude
            }
            openingHours
            phone
            email
            comingSoon
            faqs {
              question
              answer
            }
            reviewsWidget
          }
        }
      }
    }
  `;

  try {
    const response = await fetch('https://headless.catalyst-cannabis.com/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: graphqlQuery }),
    });

    if (!response.ok) {
      console.warn('WordPress GraphQL failed for locations');
      return [];
    }

    const data = await response.json();
    return data?.data?.allLocation?.nodes || [];
  } catch (error) {
    console.error('Error fetching WordPress locations:', error);
    return [];
  }
}

// Function to get current store location data from all locations
function getCurrentStoreLocation(
  store: string,
  allLocations: WordPressLocation[],
): WordPressLocation | null {
  // Map store values to location slugs (same as in locations page)
  const storeToLocationSlug: Record<string, string> = {
    catalystantioch: 'antioch-dispensary',
    catalystbellflower: 'bellflower-dispensary',
    catalystbelmont: 'belmont-shore-dispensary',
    catalyst405: 'cherry-ave-long-beach-dispensary',
    catalystcostamesa: 'costa-mesa-dispensary',
    catalystdalycity: 'daly-city-dispensary',
    catalystpine: 'downtown-long-beach-dispensary',
    catalystelmonte: 'el-monte-dispensary',
    catalystfigueroa: 'figueroa-los-angeles-dispensary',
    catalystflorence: 'florence-los-angeles-dispensary',
    catalystgroverbeach: 'grover-beach-dispensary',
    catalysthawthorne: 'hawthorne-dispensary',
    catalysthemet: 'hemet-dispensary',
    catalystlancaster: 'lancaster-dispensary',
    catalystlift: 'retro-row-long-beach-dispensary',
    catalystlynwood: 'lynwood-dispensary',
    catalystmarina: 'marina-dispensary',
    catalystmidcity: 'mid-city-los-angeles-dispensary',
    catalystmorenovalley: 'moreno-valley-dispensary',
    catalystnormandie: 'normandie-los-angeles-dispensary',
    catalystoc3: 'oc3-santa-ana-dispensary',
    catalystriverside: 'riverside-dispensary',
    catalystsacramento: 'sacramento-dispensary',
    catalystsalinas: 'salinas-dispensary',
    catalystsantaana: 'santa-ana-dispensary',
    catalystsantabarbara: 'santa-barbara-dispensary',
    catalystsantacruz: 'santa-cruz-dispensary',
    catalystsantamaria: 'santa-maria-dispensary',
    catalystsimi: 'simi-valley-dispensary',
    catalystventura: 'ventura-dispensary',
    catalystwatsonville: 'watsonville-dispensary',
    catalystwesthollywood: 'west-hollywood-dispensary',
    catalystwestminster: 'westminster-dispensary',
  };

  const locationSlug = storeToLocationSlug[store];
  if (!locationSlug) {
    console.warn(`🚨 Store mapping missing for: ${store}`);
    return null;
  }

  const foundLocation = allLocations.find((location) => location.slug === locationSlug);
  if (!foundLocation) {
    console.warn(`🚨 WordPress location not found for slug: ${locationSlug} (store: ${store})`);
  }

  return foundLocation || null;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const { slug } = params;
  const cookieHeader = request.headers.get('cookie');
  const cookies = cookieHeader ? parse(cookieHeader) : {};
  const defaultStore =
    cookies.selectedStore || new URL(request.url).searchParams.get('store') || 'catalystbellflower';

  if (!slug) {
    throw new Response('Not Found', { status: 404 });
  }

  let product: Product | null = null;
  let defaultAvailable = true;

  try {
    product = await getProductBySlug(slug, defaultStore);
    console.log(`✅ Product found at ${defaultStore}:`, product.name);
  } catch (error: any) {
    console.log(`❌ Product NOT found at ${defaultStore}:`, error.message);
    if (error.message === 'Product not found') {
      defaultAvailable = false;
    } else {
      return json({ error: error.message }, { status: 500 });
    }
  }

  // Check availability in alternative stores (exclude default)
  const availableStoresPromises = stores
    .filter((store) => store.value !== defaultStore)
    .map(async (store) => {
      try {
        const prod = await getProductBySlug(slug, store.value);
        return { store, product: prod };
      } catch (error) {
        return null;
      }
    });
  const availableStoresResults = await Promise.all(availableStoresPromises);
  const alternativeAvailable = availableStoresResults.filter(
    (item): item is { store: Store; product: Product } => item !== null,
  );
  const availableStores = alternativeAvailable.map((item) => item.store);

  // If default store doesn't have product, don't show products from other stores
  // This prevents misleading users about what's available at their selected location

  if (!product) {
    return redirect('/shop/');
  }

  // Fetch all locations for nearby dispensaries
  console.log('🔍 About to fetch all locations...');
  const allLocations = await fetchAllLocations();
  console.log('📍 Fetched locations:', allLocations.length);

  // Get current store location data for FAQs and reviews
  const currentStoreLocation = getCurrentStoreLocation(defaultStore, allLocations);
  console.log('🏪 Current store location found:', !!currentStoreLocation);

  console.log(`🎯 Final state - defaultAvailable: ${defaultAvailable}, product: ${product?.name}`);
  console.log(`📋 FAQs Debug:`, {
    store: defaultStore,
    hasCurrentStoreLocation: !!currentStoreLocation,
    hasFaqs: !!currentStoreLocation?.locationDetails?.faqs,
    faqsCount: currentStoreLocation?.locationDetails?.faqs?.length || 0,
    locationTitle: currentStoreLocation?.title,
  });

  return json<LoaderData>({
    product,
    availableStores,
    defaultAvailable,
    store: defaultStore,
    allLocations,
    currentStoreLocation,
    productUrl: new URL(request.url).origin + `/product/${product.slug}`,
  });
};

export default function ProductPage() {
  const loaderData = useLoaderData<LoaderData>();
  const {
    product,
    availableStores,
    defaultAvailable,
    store,
    allLocations,
    currentStoreLocation,
    productUrl,
  } = loaderData;
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showFullLabResults, setShowFullLabResults] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Fetch reviews client-side using ReviewProvider
  const { getReviews, isLoadingReviews, fetchReviews } = useReviewCache();
  const { getBrandLogo } = useBrandLogoCache();

  // Track if component is mounted to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);

  // Mark as mounted after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted && product?.id) {
      const cachedReviews = getReviews(product.id);
      if (!cachedReviews) {
        // Use requestAnimationFrame to ensure hydration is complete before fetching
        let rafId1: number;
        let rafId2: number;
        rafId1 = requestAnimationFrame(() => {
          // Double RAF to ensure we're past hydration
          rafId2 = requestAnimationFrame(() => {
            fetchReviews(product.id);
          });
        });
        return () => {
          cancelAnimationFrame(rafId1);
          if (rafId2) cancelAnimationFrame(rafId2);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, product?.id]);

  const reviews = getReviews(product.id) || [];

  // Calculate average rating from reviews
  const averageRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return Math.round((sum / reviews.length) * 10) / 10; // Round to 1 decimal place
  }, [reviews]);

  // Function to scroll to reviews section
  const scrollToReviews = () => {
    const reviewsElement = document.getElementById('product-reviews');
    if (reviewsElement) {
      const elementPosition = reviewsElement.offsetTop;
      const offsetPosition = elementPosition - 20; // 20px padding above

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  // Helper function to render star rating
  const renderStarRating = (rating: number, size: 'sm' | 'lg' = 'lg') => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const starSize = size === 'lg' ? 'h-[18px] w-[17.87px]' : 'h-4 w-4';

    return (
      <div className="flex items-center gap-1 px-1">
        {/* Full stars */}
        {[...Array(fullStars)].map((_, i) => (
          <img key={`full-${i}`} src="/icons/gold-leaf.svg" alt="" className={starSize} />
        ))}

        {/* Half star */}
        {hasHalfStar && <img src="/icons/gold-leaf-half.svg" alt="" className={starSize} />}

        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <img
            key={`empty-${i}`}
            src="/icons/gold-leaf.svg"
            alt=""
            className={`${starSize} opacity-30`}
          />
        ))}
      </div>
    );
  };

  // Sticky functionality for image (left) and price (right)
  const { containerRef: imageContainerRef, stickyRef: imageStickyRef } = useStickyOptimized({
    side: 'left',
  });

  const { containerRef: priceContainerRef, stickyRef: priceStickyRef } = useStickyOptimized({
    side: 'right',
  });

  // Check login status on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const getCookie = (name: string): string | null => {
        const value = '; ' + document.cookie;
        const parts = value.split('; ' + name + '=');
        if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
        return null;
      };
      const userId = getCookie('catalystUserId');
      setIsLoggedIn(!!userId);
    }
  }, []);

  // Handle variant change - navigate to new variant product page
  const handleVariantChange = async (variantId: string) => {
    try {
      // Get the current selected store
      const selectedStore = localStorage.getItem('selectedStore') || store;

      // Use existing API route to get the product data (which includes slug)
      const response = await fetch(`/api/product?productId=${variantId}&store=${selectedStore}`);

      if (!response.ok) {
        throw new Error('Failed to get product data');
      }

      const productData = await response.json();

      if (productData.error) {
        throw new Error(productData.error);
      }

      // Navigate to the new variant product page using slug
      navigate(`/product/${productData.slug}`);
    } catch (error) {
      console.error('Error getting variant product:', error);
      toast.error('Unable to load product variant');
    }
  };

  // Parse lab results from the Treez lab_results JSON array
  const parseLabResults = (labResultsString: string) => {
    if (!labResultsString || typeof labResultsString !== 'string' || !labResultsString.trim()) {
      return [];
    }
    try {
      const resultsArray = JSON.parse(labResultsString);
      return Array.isArray(resultsArray) ? resultsArray : [];
    } catch (error) {
      console.error('Error parsing lab results:', error);
      return [];
    }
  };

  // Process all lab results from Treez data
  const processLabResults = (resultsArray: any[]) => {
    const processedResults: { [key: string]: string } = {};

    resultsArray.forEach((result) => {
      const { amount, amount_type, result_type } = result;

      if (result_type && amount && amount.minimum_value) {
        const formattedAmount =
          amount_type === 'PERCENTAGE'
            ? `${parseFloat(amount.minimum_value).toFixed(2)}%`
            : `${parseFloat(amount.minimum_value).toFixed(2)}${amount_type || ''}`;

        // Normalize result type names for display
        let displayName = result_type;
        if (result_type === 'DELTA-9_THC' || result_type === 'THCA') {
          displayName = 'THC';
        } else if (result_type === 'CBDA') {
          displayName = 'CBD';
        }

        processedResults[displayName] = formattedAmount;
      }
    });

    return processedResults;
  };

  const rawLabResults = parseLabResults(product.lab_results || '[]');
  const allLabResults = processLabResults(rawLabResults);

  // Helper function to check if a value is meaningful (not 0 or empty)
  const isMeaningfulValue = (value: string | null | undefined): boolean => {
    if (!value) return false;
    const trimmed = value.toString().trim();
    if (trimmed === '' || trimmed === '0' || trimmed === '0%' || trimmed === '0.00%') return false;
    const numericValue = parseFloat(trimmed.replace('%', ''));
    return !isNaN(numericValue) && numericValue > 0;
  };

  // Primary results (only show THC and CBD if they have meaningful values)
  const primaryResults = [
    ...(isMeaningfulValue(allLabResults.THC || product.thc)
      ? [{ label: 'THC', value: allLabResults.THC || product.thc }]
      : []),
    ...(isMeaningfulValue(allLabResults.CBD || product.cbd)
      ? [{ label: 'CBD', value: allLabResults.CBD || product.cbd }]
      : []),
  ].filter(Boolean);

  // Additional lab results (everything except THC and CBD)
  const additionalResults = Object.entries(allLabResults)
    .filter(([key]) => !['THC', 'CBD'].includes(key))
    .map(([key, value]) => ({
      label: key,
      value: String(value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

  // TODO: BACKEND TEAM - Add more comprehensive lab results to Treez integration
  // Currently only THC and CBD are available. The system is ready to display:
  // - CBN, CBG, CBC, CBDV, THCV (other cannabinoids)
  // - Terpenes (Limonene, Myrcene, Pinene, etc.)
  // - Any other compounds from lab testing
  // The dropdown will automatically appear when additional lab results are available
  // in the product.lab_results JSON array from Treez

  // 🔥 GA4 Enhanced Ecommerce - view_item event
  useEffect(() => {
    if (product) {
      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackViewItemComplete({
        id: product.id.toString(),
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.salePrice || product.price,
      });
    }
  }, [product]);
  const { openCart, incrementCount } = useCart();
  const navigate = useNavigate();

  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () => {
    if (quantity > 1) setQuantity((prev) => prev - 1);
  };

  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const selectedStore = localStorage.getItem('selectedStore');
      const order_id = selectedStore
        ? localStorage.getItem(`token_${selectedStore}`) || 'default_order_id'
        : 'default_order_id';

      const payload = {
        actionType: 'ADD',
        order_id,
        product_id: product.id,
        quantity: String(quantity),
      };

      const response = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseData = await response.clone().json();
      
      // Check for errors in response body (API returns 200 with errors)
      if (responseData?.error && response.ok) {
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        throw new Error(parsedError.message);
      }
      
      if (!response.ok) {
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        throw new Error(parsedError.message);
      }
      await response.json();
      incrementCount(quantity);

      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackAddToCartComplete({
        id: product.id.toString(),
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.salePrice || product.price,
        quantity: quantity,
      });

      openCart();
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      // Only show generic toast if error wasn't already handled
      if (!isErrorAlreadyHandled(error)) {
        toast.error('Failed to add item to cart. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Format prices for display (with $ symbol)
  const formattedPrice = product.price != null ? `$${product.price.toFixed(2)}` : 'N/A';
  const formattedSalePrice = product.salePrice != null ? `$${product.salePrice.toFixed(2)}` : null;

  // Numeric prices for Schema.org (without $ symbol)
  const numericPrice = product.price != null ? product.price.toFixed(2) : null;
  const numericSalePrice = product.salePrice != null ? product.salePrice.toFixed(2) : null;

  // Memoized discount calculation for performance
  const discountPercentage = useMemo(() => {
    return product.salePrice && product.salePrice < product.price
      ? Math.round(((product.price - product.salePrice) / product.price) * 100)
      : null;
  }, [product.price, product.salePrice]);

  return (
    <>
      <div className="mx-auto w-full max-w-[108rem]">
        {/* Skip Navigation Link for accessibility */}
        <a
          href="#main-content"
          className="sr-only z-50 rounded bg-blue-600 px-4 py-2 text-white focus:not-sr-only focus:absolute focus:top-4 focus:left-4"
        >
          Skip to main content
        </a>

        {/* Breadcrumbs Navigation */}
        <nav aria-label="Breadcrumb" className="w-full px-4 py-4 md:px-6">
          <div className="mx-auto max-w-[100rem]">
            <ol className="flex items-center gap-2.5">
              {/* Home */}
              <li className="flex items-center gap-2.5">
                <Link
                  to="/"
                  className="flex items-center rounded text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Go to homepage"
                >
                  <img
                    src="/icons/home-icon.svg"
                    alt="Home navigation"
                    aria-hidden="true"
                    className="h-4 w-4"
                  />
                </Link>
                <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                  /
                </span>
              </li>

              {/* Shop */}
              <li className="flex items-center gap-2.5">
                <Link
                  to="/shop"
                  className="rounded text-sm font-medium text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Shop
                </Link>
                <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                  /
                </span>
              </li>

              {/* Category */}
              {product.category && (
                <li className="flex items-center gap-2.5">
                  <Link
                    to={`/category/${product.category.toLowerCase()}`}
                    className="rounded text-sm font-medium text-[#264B9A] hover:underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {product.category
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')}
                  </Link>
                  <span className="text-xs font-light text-[#8D86B8]" aria-hidden="true">
                    /
                  </span>
                </li>
              )}

              {/* Current Product */}
              <li>
                {/* Truncated name for small screens only */}
                <span className="text-sm font-light text-[#1E293B] md:hidden" aria-current="page">
                  {(() => {
                    const titleCaseName = product.name
                      .split(' ')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ');

                    const maxLength = 16;
                    return titleCaseName.length > maxLength
                      ? `${titleCaseName.substring(0, maxLength).trim()}...`
                      : titleCaseName;
                  })()}
                </span>

                {/* Full name for md screens and above */}
                <span
                  className="hidden text-sm font-light text-[#1E293B] md:inline"
                  aria-current="page"
                >
                  {product.name
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ')}
                </span>
              </li>
            </ol>
          </div>
        </nav>
      </div>

      {/* Schema.org Microdata Wrapper */}
      <div itemScope itemType="https://schema.org/Product" id="main-content">
        <section className="product-card font-futura relative mx-auto flex w-full max-w-[100rem] flex-col bg-white px-4 lg:p-6">
          {/* Product Name - Mobile Only */}
          <header className="mb-2 lg:hidden">
            <h1
              className="font-poppins text-2xl leading-8 font-semibold text-[#0F172A] normal-case"
              itemProp="name"
            >
              {product.name
                ? product.name
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ')
                : 'N/A'}
            </h1>

            {/* Reviews Section - Only show button for logged-in users */}
            {/* Write Review Button - Mobile, always reserve space */}
            <div className={` ${!isLoggedIn ? 'invisible' : ''}`}>
              <button
                type="button"
                onClick={() => setIsReviewModalOpen(true)}
                className="font-helvetica-neue inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                aria-label={`Leave a review for ${product.name}`}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
                Leave a Review
              </button>
            </div>

            {/* Star Rating - Mobile Only - Always reserve space */}
            <div className="flex w-full items-center justify-between">
              <div className={`flex items-center gap-1 ${reviews.length === 0 ? 'invisible' : ''}`}>
                <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                  {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
                </span>
                <div className="flex items-center gap-1">
                  {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                  <button
                    onClick={scrollToReviews}
                    className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                    aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                  >
                    ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <span className="font-helvetica-neue text-xs leading-4 font-normal text-black">
                  {product.size || '3.5G'}
                </span>
              </div>
            </div>
          </header>

          <div className="mb-6 flex flex-col lg:flex-row lg:gap-16" ref={imageContainerRef}>
            {/* Left Column - Product Image */}
            <div className="w-full lg:w-[35%]">
              <div className="mx-auto w-full max-w-[31.25rem]" ref={imageStickyRef}>
                <div key={product.id}>
                  <figure
                    className="relative aspect-square w-full max-w-[31.25rem] rounded-md shadow-lg"
                    itemProp="image"
                  >
                    {discountPercentage !== null && (
                      <span className="sale absolute top-3 left-3 z-10 rounded bg-[#ffd100] px-2 py-2 text-xs font-bold shadow-sm">
                        {discountPercentage}% OFF
                      </span>
                    )}

                    <img
                      src={`${product.image || getFillerImage(product.brand, product.category, getBrandLogo(product.brand))}?v=${product.id}`}
                      alt={product.name || 'N/A'}
                      className="h-full w-full rounded-md object-cover"
                      itemProp="image"
                      loading="eager"
                      onError={(e) => {
                        console.log('Product page image failed to load:', {
                          src: e.currentTarget.src,
                          product: {
                            name: product.name,
                            brand: product.brand,
                            category: product.category,
                          },
                        });
                        // Prevent infinite error loops
                        e.currentTarget.onerror = null;

                        // Try brand logo fallback first, then default image
                        const brandFallback = getFillerImage(
                          product.brand,
                          product.category,
                          getBrandLogo(product.brand),
                        );

                        if (
                          !e.currentTarget.src.includes('/images/default.webp') &&
                          e.currentTarget.src !== brandFallback
                        ) {
                          console.log(
                            'Product page switching to brand logo fallback:',
                            brandFallback,
                          );
                          e.currentTarget.src = brandFallback;
                        } else if (!e.currentTarget.src.includes('/images/default.webp')) {
                          console.log(
                            'Product page brand fallback failed, switching to default image',
                          );
                          e.currentTarget.src = '/images/default.webp';
                        } else {
                          console.warn('Product page default fallback image also failed to load');
                        }
                      }}
                    />
                  </figure>
                </div>
              </div>
            </div>

            {/* Mobile Price Section - Under image on mobile only */}
            <div className="mx-auto mt-6 w-full max-w-[31.25rem] lg:hidden">
              <div className="flex w-full flex-col items-start gap-5 bg-white py-3">
                {/* Price & Quantity Row */}
                <div className="flex w-full items-start justify-center gap-4 px-11">
                  {/* Price Section - Left side */}
                  <div className="flex flex-1 flex-col items-center justify-center gap-5">
                    <div className="flex flex-col items-center justify-end gap-2.5">
                      <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                        ${(product.salePrice || product.price).toFixed(2)}
                      </span>
                      {product.salePrice && (
                        <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                          ${product.price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quantity Controls - Right side */}
                  <div className="flex flex-1 items-start justify-center gap-7 rounded-lg pt-2">
                    <button
                      onClick={decrementQuantity}
                      className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                      {quantity}
                    </span>
                    <button
                      onClick={incrementQuantity}
                      className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Add to Cart Button - Full width at bottom */}
                <button
                  onClick={handleAddToCart}
                  disabled={isLoading}
                  className="font-helvetica-neue flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] px-2 py-4 text-base leading-4 font-medium text-white hover:bg-[#1e3a7a] disabled:opacity-50"
                >
                  {isLoading ? 'Adding...' : 'Add To Cart'}
                </button>
              </div>
            </div>

            {/* Middle Column - Product Details */}
            <div className="mt-2 flex flex-col md:mt-4 lg:mt-0 lg:w-[40%]">
              {/* Product Header */}
              <div className="flex flex-col items-start">
                {/* Product Name - Desktop Only */}
                <header className="hidden w-full lg:block">
                  <h1
                    className="font-poppins text-[32px] leading-10 font-semibold text-[#0F172A] normal-case"
                    itemProp="name"
                  >
                    {product.name
                      ? product.name
                          .split(' ')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')
                      : 'N/A'}
                  </h1>
                </header>

                {/* Reviews Section - Only show button for logged-in users */}
                {/* Write Review Button - Desktop, always reserve space */}
                <div className={`hidden lg:block ${!isLoggedIn ? 'invisible' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setIsReviewModalOpen(true)}
                    className="font-helvetica-neue mt-2 inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                    aria-label={`Leave a review for ${product.name}`}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                    Leave a Review
                  </button>
                </div>

                {/* Star Rating - Desktop Only - Always reserve space */}
                <div
                  className={`hidden w-full items-center gap-1 lg:flex ${reviews.length === 0 ? 'invisible' : ''}`}
                >
                  <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                    {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
                  </span>
                  <div className="flex items-center gap-1">
                    {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                    <button
                      onClick={scrollToReviews}
                      className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                      aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                    >
                      ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Tags */}
              <div className="mb-6 flex flex-col items-start gap-3 pt-3">
                <div className="flex w-full flex-row items-start gap-3 lg:flex-col lg:gap-3">
                  <div className="flex items-start gap-1">
                    <span className="font-helvetica-neue text-lg leading-[21px] font-normal text-[#6B7280]">
                      Category:
                    </span>
                    <span className="font-helvetica-neue text-lg leading-[21px] font-normal text-black">
                      {product.category
                        ? product.category
                            .split(' ')
                            .map(
                              (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                            )
                            .join(' ')
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-start gap-1">
                    <span className="font-helvetica-neue text-lg leading-[21px] font-normal text-[#6B7280]">
                      Subtype:
                    </span>
                    <span className="font-helvetica-neue text-lg leading-[21px] font-normal text-black">
                      {product.subtype
                        ? product.subtype
                            .split(' ')
                            .map(
                              (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                            )
                            .join(' ')
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Product Variants Section */}
              {product.budflow_flavors && (
                <ProductVariants
                  currentProductId={product.id}
                  currentProductName={product.name}
                  budflowFlavors={product.budflow_flavors}
                  onVariantChange={handleVariantChange}
                />
              )}

              {/* Product Description Accordion */}
              {product.product_description && (
                <div className="mb-6 flex flex-col items-start gap-[14px] bg-white">
                  <div className="flex w-full items-center gap-8 pr-2">
                    <h2 className="font-helvetica-neue text-xl leading-6 font-medium text-black">
                      Product Description
                    </h2>
                  </div>
                  <div className="flex w-full flex-col items-center gap-[6px]">
                    <div className="flex w-full flex-col items-start gap-[14px] pb-2.5">
                      <div
                        className="font-helvetica-neue w-full text-base leading-6 font-light tracking-[0.02em] text-black"
                        itemProp="description"
                      >
                        {product.product_description}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Lab Results Accordion */}
              <div className="mb-6 flex flex-col items-start gap-4 bg-white pt-6">
                <div className="flex w-full flex-col gap-2">
                  <div className="flex w-full items-center gap-8 pr-2">
                    <h2 className="font-helvetica-neue text-center text-xl leading-6 font-medium text-black">
                      Lab Results
                    </h2>
                  </div>
                  {/* Batch Variation Disclaimer */}
                  <p className="font-helvetica-neue text-sm leading-5 font-normal text-[#6B7280] italic">
                    Lab results may vary by batch. Actual product may differ from displayed values.
                  </p>
                </div>
                <div className="flex w-full flex-col items-start gap-[6px]">
                  <div className="flex w-full flex-col items-start pb-3">
                    {/* Primary Lab Results - THC and CBD */}
                    <div className="flex w-full">
                      {primaryResults.map((result, index) => (
                        <div
                          key={result.label}
                          className={`flex flex-1 items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                        >
                          <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                            {result.label}:
                          </span>
                          <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                            {result.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Additional Lab Results - Shown when expanded */}
                    {showFullLabResults && additionalResults.length > 0 && (
                      <div className="mt-2 flex w-full flex-col">
                        {additionalResults.map((result, index) => (
                          <div
                            key={result.label}
                            className={`flex w-full items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                          >
                            <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                              {result.label}:
                            </span>
                            <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                              {result.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Toggle Button - Only show if there are additional results */}
                  {additionalResults.length > 0 && (
                    <button
                      onClick={() => setShowFullLabResults(!showFullLabResults)}
                      className="flex w-full items-center justify-center gap-2 bg-white py-1 pr-2 transition-colors hover:bg-gray-50"
                    >
                      <span className="font-helvetica-neue text-base leading-5 font-medium text-[#264B9A]">
                        {showFullLabResults ? 'Show Less' : 'Read Full List'}
                      </span>
                      <div className="h-[14px] w-[14px]">
                        {showFullLabResults ? (
                          // Chevron Up
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M3.5 8.75L7 5.25L10.5 8.75"
                              stroke="#264B9A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          // Chevron Down
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M3.5 5.25L7 8.75L10.5 5.25"
                              stroke="#264B9A"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  )}
                </div>
                <div className="h-0.5 w-full bg-[#EBEBE6]"></div>
              </div>

              {/* Product Reviews Section */}
              <div className="mb-6 w-full">
                <ProductReviews
                  productId={product.id}
                  onWriteReviewClick={() => setIsReviewModalOpen(true)}
                  isLoggedIn={isLoggedIn}
                />
              </div>

              {/* Store FAQs Section */}
              {currentStoreLocation?.locationDetails?.faqs &&
                currentStoreLocation.locationDetails.faqs.length > 0 && (
                  <div className="mb-6 w-full">
                    <div className="w-full [&>section]:w-full [&>section>div]:w-full [&>section>div]:max-w-none">
                      <FaqsSection
                        faqs={currentStoreLocation.locationDetails.faqs}
                        locationName={currentStoreLocation.title}
                        showLocationSpecificContent={false}
                      />
                    </div>
                  </div>
                )}
            </div>

            {/* Right Column - Price & Purchase - Desktop only */}
            <div className="hidden lg:block lg:w-[25%]" ref={priceContainerRef}>
              <div
                className="mx-auto flex w-full max-w-[310px] flex-col items-center gap-4 rounded-lg bg-white p-6 shadow-[0px_2px_6px_rgba(0,0,0,0.15)]"
                ref={priceStickyRef}
              >
                {/* Price Section */}
                <div
                  className="flex w-full flex-col items-center justify-center gap-5"
                  itemProp="offers"
                  itemScope
                  itemType="https://schema.org/Offer"
                >
                  <meta itemProp="priceCurrency" content="USD" />
                  {numericSalePrice ? (
                    <meta itemProp="price" content={numericSalePrice} />
                  ) : numericPrice ? (
                    <meta itemProp="price" content={numericPrice} />
                  ) : null}
                  <div className="font-helvetica-neue flex flex-row items-end justify-center gap-2.5">
                    {formattedSalePrice ? (
                      <>
                        <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                          {formattedSalePrice}
                        </span>
                        <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                          {formattedPrice}
                        </span>
                      </>
                    ) : (
                      <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                        {formattedPrice}
                      </span>
                    )}
                  </div>
                  <meta itemProp="availability" content="https://schema.org/InStock" />
                </div>

                {/* Quantity Info */}
                <div className="flex w-full items-center justify-center">
                  <span className="text-xs leading-4 font-normal text-black">
                    {product.size || 'N/A'}
                  </span>
                </div>

                {/* Low Stock Alert */}
                <LowStockAlert
                  stock={product.stock}
                  sellableQuantity={product.sellable_quantity}
                  className="w-full"
                />

                {/* Buttons Section */}
                {defaultAvailable ? (
                  <div className="flex w-full flex-col items-center gap-4">
                    {/* Quantity Controls */}
                    <div className="flex h-12 w-full items-center justify-center gap-7 rounded-lg p-4">
                      <button
                        onClick={decrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                        {quantity}
                      </span>
                      <button
                        onClick={incrementQuantity}
                        className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={handleAddToCart}
                      aria-label={`Add ${quantity} ${quantity === 1 ? 'item' : 'items'} of ${product.name} to cart`}
                      className="relative flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] p-4 text-base leading-4 font-medium text-white before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:bg-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                    >
                      {isLoading ? 'Adding...' : 'Add To Cart'}
                    </button>
                  </div>
                ) : (
                  <div className="w-full rounded-lg bg-gray-100 p-4 text-center">
                    <p className="font-bold text-red-600">
                      Sorry, this product is out of stock at your selected location, please try one
                      of our alternative locations below.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Structured Data (JSON-LD) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: product.name,
              image:
                product.image ||
                getFillerImage(product.brand, product.category, getBrandLogo(product.brand)),
              description: product.product_description || '',
              sku: product.id,
              brand: {
                '@type': 'Brand',
                name: product.brand || 'Unknown',
              },
              offers: {
                '@type': 'Offer',
                url: productUrl,
                priceCurrency: 'USD',
                price: product.salePrice ?? product.price,
                availability: 'https://schema.org/InStock',
              },
            }),
          }}
        ></script>

        {/* Shop Nearby Dispensaries Section */}
        <ShopNearbyDispensaries currentStore={store} allLocations={allLocations} />

        {/* Product Review Modal */}
        {isLoggedIn && (
          <ProductReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => setIsReviewModalOpen(false)}
            product={{
              id: product.id,
              name: product.name || 'Product',
              image:
                product.image ||
                getFillerImage(product.brand, product.category, getBrandLogo(product.brand)),
              brand: product.brand,
            }}
          />
        )}
      </div>
    </>
  );
}
